/**
 * App Controller - Manages authentication and app initialization
 *
 * Handles:
 * - Auth state management
 * - Database initialization for authenticated users
 * - Sync service initialization
 */

import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { authService, type AuthState } from "../services/auth";
import { syncService } from "../services/sync";
import { dbManager } from "../db/user-database";
import { Router } from "@vaadin/router";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";

import "../pages/login-page";

@customElement("app-controller")
export class AppController extends LitElement {
  @state() private authState: AuthState = {
    isAuthenticated: false,
    isLoading: true,
  };
  @state() private isInitializing = true;
  @state() private initError = "";

  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
    }

    .loading-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .loading-screen sl-spinner {
      font-size: 2rem;
      --indicator-color: white;
      --track-color: rgba(255, 255, 255, 0.3);
    }

    .loading-screen p {
      margin-top: 1rem;
      font-size: 1rem;
    }

    .error-screen {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: #f8d7da;
      color: #721c24;
      padding: 2rem;
      text-align: center;
    }

    .error-screen h2 {
      margin: 0 0 1rem 0;
    }

    .error-screen button {
      margin-top: 1rem;
      padding: 0.5rem 1rem;
      background: #0d6efd;
      color: white;
      border: none;
      border-radius: 0.25rem;
      cursor: pointer;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  private async initialize() {
    try {
      // Subscribe to auth state changes
      authService.subscribe((state) => {
        this.authState = state;
        this.handleAuthStateChange(state);
      });

      // Initialize auth service (checks for existing session)
      await authService.initialize();
      this.isInitializing = false;
    } catch (error) {
      console.error("Initialization failed:", error);
      this.initError =
        error instanceof Error ? error.message : "Initialization failed";
      this.isInitializing = false;
    }
  }

  private async handleAuthStateChange(state: AuthState) {
    if (state.isAuthenticated && state.userId) {
      // User logged in - initialize their database and sync
      try {
        await dbManager.switchToUserDatabase(state.userId);
        await syncService.initialize();
        
        // Check for pending invite from accept-invite-page
        const pendingInvite = sessionStorage.getItem("pendingInvite");
        if (pendingInvite) {
          sessionStorage.removeItem("pendingInvite");
          // Small delay to ensure router is ready
          requestAnimationFrame(() => {
            Router.go(`/invite/${pendingInvite}`);
          });
        }
      } catch (error) {
        console.error("Failed to initialize user database:", error);
      }
    } else if (!state.isAuthenticated && !state.isLoading) {
      // User logged out - cleanup
      syncService.cleanup();
      await dbManager.close();
    }
  }

  private handleAuthSuccess() {
    // Auth state change will be handled by subscription
  }

  private handleRetry() {
    this.isInitializing = true;
    this.initError = "";
    this.initialize();
  }

  render() {
    // Show loading screen during initialization
    if (this.isInitializing) {
      return html`
        <div class="loading-screen">
          <sl-spinner></sl-spinner>
          <p>Loading DAFNE Log...</p>
        </div>
      `;
    }

    // Show error screen if initialization failed
    if (this.initError) {
      return html`
        <div class="error-screen">
          <h2>Something went wrong</h2>
          <p>${this.initError}</p>
          <button @click=${this.handleRetry}>Try Again</button>
        </div>
      `;
    }

    // Show login page if not authenticated
    if (!this.authState.isAuthenticated) {
      return html`<login-page
        @auth-success=${this.handleAuthSuccess}
      ></login-page>`;
    }

    // Show main app
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-controller": AppController;
  }
}
